import Logo from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { register } from '@/lib/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import libphonenumber from 'google-libphonenumber';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();
export const semIdRegex = /^[0-9]{8}$/i;

const formSchema = z.object({
  firstName: z.string().min(1, {
    message: 'Vezetéknév megadása kötelező.',
  }),
  lastName: z.string().min(1, {
    message: 'Keresztnév megadása kötelező.',
  }),
  email: z.string().email({
    message: 'Hibás email cím.',
  }),
  phone: z.string().refine(
    (number) => {
      try {
        const phoneNumber = phoneUtil.parse(number);
        return phoneUtil.isValidNumber(phoneNumber);
      } catch (error) {
        return false;
      }
    },
    { message: 'Invalid phone number' }
  ),
  password: z.string().min(6, {
    message: 'Jelszó megadása szükséges.',
  }),
  confirmPassword: z.string().min(6, {
    message: 'Jelszó megerősítése szükséges.',
  }),
  partnerName: z.string().min(1, {
    message: 'Partner neve megadása kötelező.',
  }),
  partnerAddress: z.string().min(1, {
    message: 'Partner címe megadása kötelező.',
  }),
  partnerDeliveryName: z.string().min(1, {
    message: 'Partner neve megadása kötelező.',
  }),
  partnerDeliveryAddress: z.string().min(1, {
    message: 'Partner címe megadása kötelező.',
  }),
  semId: z.string().regex(semIdRegex, 'Partnerkód nem megfelelő formátumú.'),
  terms: z.boolean().refine((data) => data === true, {
    message: 'El kell fogadnod a feltételeket.',
  }),
});

export default function Register() {
  const [isSuccess, setIsSuccess] = useState(false);
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      partnerName: '',
      partnerAddress: '',
      partnerDeliveryName: '',
      partnerDeliveryAddress: '',
      semId: '',
      password: '',
      confirmPassword: '',
      terms: false,
    },
    // defaultValues: {
    //   firstName: 'Teszt',
    //   lastName: 'Elek',
    //   email: 'titusz+1@modolit.com',
    //   phone: '+36 20 123 4567',
    //   partnerName: 'Teszt Elek',
    //   partnerAddress: '1234 Budapest, Test utca 1',
    //   partnerDeliveryName: 'Teszt Elek',
    //   partnerDeliveryAddress: '1234 Budapest, Test utca 1',
    //   semId: '00011112',
    //   password: 'password123',
    //   confirmPassword: 'password123',
    //   terms: true,
    // },
  });

  const renderMobileFormFields = () => {
    return (
      <div className="space-y-4 w-full">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Vezetéknév</FormLabel>
              <FormControl>
                <Input placeholder="Kovács" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Keresztnév</FormLabel>
              <FormControl>
                <Input placeholder="János" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Email cím</FormLabel>
              <FormControl>
                <Input placeholder="pelda@email.hu" type="email" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Telefonszám</FormLabel>
              <FormControl>
                <Input placeholder="+36 20 123 4567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Jelszó</FormLabel>
              <FormControl>
                <Input type="password" placeholder="********" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="confirmPassword"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Jelszó megerősítése</FormLabel>
              <FormControl>
                <Input type="password" placeholder="********" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="partnerName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Partner neve</FormLabel>
              <FormControl>
                <Input placeholder="Partner neve" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="partnerAddress"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Partner címe</FormLabel>
              <FormControl>
                <Input placeholder="Partner címe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="partnerDeliveryName"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Partner szállítási neve</FormLabel>
              <FormControl>
                <Input placeholder="Partner szállítási neve" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="partnerDeliveryAddress"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>Partner szállítási címe</FormLabel>
              <FormControl>
                <Input placeholder="Partner szállítási címe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="semId"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel>SemId</FormLabel>
              <FormControl>
                <Input placeholder="1234567890" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    );
  };

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      const response = await register(values);
      if (response.message === 'User registered successfully') {
        setIsSuccess(true);
      }
    } catch (error) {
      console.error(error);
    }
  }

  if (isSuccess) {
    return (
      <div className="w-full min-h-screen p-6 py-10 md:p-20 text-white bg-gradient-to-r from-heineken-green via-heineken-light-green to-heineken-dark-green">
        <div className="max-w-3xl mx-auto flex flex-col items-center">
          <Logo className="mb-10" />

          <h1 className="font-heineken font-bold text-4xl md:text-5xl text-center">
            Regisztráció sikeres
          </h1>
          <p className="text-center mt-6 mb-10">
            A regisztráció megerősítéséhez szükséges linket elküldtük az email
            címedre. Kérjük, ellenőrizd a beérkező leveleket és kövesd az
            utasításokat.
          </p>

          <Link to="/login" className="underline">
            Vissza a bejelentkezéshez
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen p-6 py-10 md:p-20 text-white bg-gradient-to-r from-heineken-green via-heineken-light-green to-heineken-dark-green">
      <div className="max-w-3xl mx-auto flex flex-col items-center">
        <Logo className="mb-10" />

        <h1 className="font-heineken font-bold text-4xl md:text-5xl text-center">
          Regisztráció
        </h1>
        <p className="text-center mt-6 mb-2 font-bold">
          Ügyelj rá, hogy a regisztráció során megadott adatok pontosak
          legyenek, helytelen adatokkal nem tudod beváltani a kreditjeidet.
        </p>
        <p className="text-center mt-6 mb-10">
          Információ: a SEM ID a Heineken partnerkódod – ha nem ismered, kérd a
          területi képviselőd segítségét!
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
            <div className="md:hidden">{renderMobileFormFields()}</div>

            <div className="hidden md:grid md:grid-cols-2 gap-4">
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Vezetéknév</FormLabel>
                      <FormControl>
                        <Input placeholder="Kovács" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Email cím</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="pelda@email.hu"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="w-full md:col-span-1">
                      <FormLabel>Jelszó</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="********"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Partner neve</FormLabel>
                      <FormControl>
                        <Input placeholder="Partner neve" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerDeliveryName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Partner szállítási neve</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Partner szállítási neve"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Second Column */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Keresztnév</FormLabel>
                      <FormControl>
                        <Input placeholder="János" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Telefonszám</FormLabel>
                      <FormControl>
                        <Input placeholder="+36 20 123 4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem className="w-full md:col-span-1">
                      <FormLabel>Jelszó megerősítése</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="********"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="partnerAddress"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Partner címe</FormLabel>
                      <FormControl>
                        <Input placeholder="Partner címe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="partnerDeliveryAddress"
                  render={({ field }) => (
                    <FormItem className="w-full">
                      <FormLabel>Partner szállítási címe</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Partner szállítási címe"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Full Width Fields for Desktop */}
              <FormField
                control={form.control}
                name="semId"
                render={({ field }) => (
                  <FormItem className="w-full md:col-span-2">
                    <FormLabel>SemId</FormLabel>
                    <FormControl>
                      <Input placeholder="1234567890" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Terms and Submit - Common for both layouts */}
            <FormField
              control={form.control}
              name="terms"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center w-full mt-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      className="size-[24px]"
                    />
                  </FormControl>
                  <FormLabel className="block ml-2">
                    A regisztrációval elfogadod a{' '}
                    <Link to="/rules" className="underline">
                      játékszabályokat
                    </Link>{' '}
                    és{' '}
                    <Link to="/privacy" className="underline">
                      adatkezelési tájékoztatót
                    </Link>
                  </FormLabel>
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2 w-full items-end mt-4">
              <Button
                type="submit"
                variant="light"
                disabled={
                  form.formState.isSubmitting || !form.formState.isValid
                }
                className="w-full md:w-auto"
              >
                Regisztráció
              </Button>
            </div>
          </form>
        </Form>

        <p className="text-sm mt-6 w-full text-left">
          Van már fiókom,{' '}
          <Link to="/login" className="underline">
            belépek
          </Link>
        </p>
      </div>
    </div>
  );
}
